import type { QueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/lib/constants';

// Which cached views go stale after each kind of mutation is decided here and nowhere else: hooks
// call one of the functions below instead of listing keys, so a new derived view is a one-line
// edit in this file rather than a find-and-add across every mutation.
//
// The promises are discarded on purpose: invalidateQueries only rejects when throwOnError is set
// (it isn't), and returning them from onSuccess would keep each mutation pending until every
// refetch lands — delaying callers' own onSuccess, e.g. closing an inline edit form.
function invalidate(queryClient: QueryClient, ...keys: string[]): void {
  for (const key of keys) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

// A transaction was created, edited, deleted or imported: the list, plus the two views derived from it.
export function invalidateFinancialQueries(queryClient: QueryClient): void {
  invalidate(queryClient, QUERY_KEYS.transactions, QUERY_KEYS.reports, QUERY_KEYS.budgetSummary);
}

// A category was created or edited: the list, plus the views that embed it (reports) or fold by its
// budget assignment (budget summary). Not branched on which field changed, or on create vs. update —
// a missed branch is a silent stale-data bug, and inactive queries are only marked stale.
export function invalidateCategoryQueries(queryClient: QueryClient): void {
  invalidate(queryClient, QUERY_KEYS.categories, QUERY_KEYS.reports, QUERY_KEYS.budgetSummary);
}

// The goal set was saved. The goals themselves are seeded from the PUT response, so only the summary
// — which applies them to the period's income — needs refetching.
export function invalidateBudgetSummary(queryClient: QueryClient): void {
  invalidate(queryClient, QUERY_KEYS.budgetSummary);
}
